import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Inbox, Search, Pause, X, Download, ChevronDown, Pencil } from 'lucide-react';
import { api, apiDownload, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  ClientContacts,
  ConfidenceBadge,
  ConfidenceControl,
  Drawer,
  EmptyState,
  EntityLink,
  Input,
  Modal,
  Select,
  Skeleton,
  StageColumn,
  StatusPill,
  TextArea,
  TransitionMenu,
  ViewToggle,
  useToast,
  type InquiriesView,
  type StageColumnTone,
  type StatusTone,
  type TransitionInput,
} from '../components';
import { PageHeader } from '../layouts/PageHeader';
import type {
  Client,
  Confidence,
  CriteriaBasket,
  Inquiry,
  InquiryAttachment,
  InquiryCriteriaResponse,
  InquirySource,
  InquiryTransition,
  InquiryType,
  Integrator,
  ProductType,
  Stage,
  User,
} from '../types/api';
import {
  CONFIDENCE_REQUIRED_STAGES,
  EDUCATIONAL_ROUTE,
  INQUIRY_TYPES,
  STAGE_ORDER,
  generalContractorDisplay,
  inquiryAmount,
  visibleInquiryAmount,
  inquiryRef,
  inquiryTitle,
  nextStageFor,
  stageShowsDealParams,
  stageShowsGeneralContractor,
  stageShowsTimer,
} from '../types/api';
import { resolveDrop, type BoardColumnKey, type DndTransitionInput } from '../lib/boardDnd';
import { NEXT_STEP_BUCKETS, groupByNextStep, overdueCount, type NextStepBucket } from '../lib/nextStep';
import { InquiriesDndDialog } from './InquiriesDndDialog';
import { InquiriesCalendar } from './InquiriesCalendar';
import { AttachmentsSection } from '../components/AttachmentsSection';
import { DealParamsSection } from '../components/DealParamsSection';
import { CriteriaSection, BASKET_LABEL_KEY, criteriaQueryKey } from '../components/CriteriaSection';
import { StageProgress } from '../components/DealCard/StageProgress';
import { useT } from '../i18n';
import type { TranslationKey } from '../i18n';
import { formatAge, formatDate, formatDateTime, formatMoney } from '../lib/format';
import { ageTone } from '../lib/age';
import { STAGE_LABEL_KEY, stageLabelKey } from '../lib/stageLabels';
import styles from './Inquiries.module.css';

const SOURCES: { value: InquirySource; labelKey: TranslationKey }[] = [
  { value: 'end_customer', labelKey: 'inquiries.sourceEndCustomer' },
  { value: 'integrator', labelKey: 'inquiries.sourceIntegrator' },
  { value: 'partner_chkpz', labelKey: 'inquiries.sourcePartnerChkpz' },
  { value: 'email', labelKey: 'inquiries.sourceEmail' },
];

const TYPE_LABEL_KEY: Record<InquiryType, TranslationKey> = {
  QUESTION: 'inquiryType.question',
  REQUEST: 'inquiryType.request',
  INVITATION: 'inquiryType.invitation',
  PARTNERSHIP: 'inquiryType.partnership',
  SALE_OFFER: 'inquiryType.saleOffer',
  GISP: 'inquiryType.gisp',
  AUDIT_REQUEST: 'inquiryType.auditRequest',
  SOLUTION_HELP: 'inquiryType.solutionHelp',
};

/**
 * Display-level step labels live in lib/stageLabels (shared with the
 * Dashboard funnel, #74); the maps below are canonical wording only.
 */

const STAGE_TONE: Record<Stage, StageColumnTone> = {
  VALIDATION: 'validation',
  VERIFICATION: 'verification',
  PRIORITIZATION: 'prioritization',
  REALIZATION: 'realization',
  INTEGRATOR_SELECTION: 'integrator_selection',
  TKP: 'tkp',
  // Issue #80 — the industrial «Контракт» column reuses the ТКП tone
  // (same late-deal family); a dedicated visual, if wanted, is a design call.
  CONTRACT: 'tkp',
};

const STAGE_PILL_TONE: Record<Stage, StatusTone> = {
  VALIDATION: 'stage_validation',
  VERIFICATION: 'stage_verification',
  PRIORITIZATION: 'stage_prioritization',
  REALIZATION: 'stage_realization',
  INTEGRATOR_SELECTION: 'stage_integrator_selection',
  TKP: 'stage_tkp',
  CONTRACT: 'stage_tkp',
};

/** Issue #52 — activity («По делам») column labels + tints. */
const NEXT_STEP_LABEL_KEY: Record<NextStepBucket, TranslationKey> = {
  overdue: 'activity.overdue',
  today: 'activity.today',
  thisWeek: 'activity.thisWeek',
  nextWeek: 'activity.nextWeek',
  noDate: 'activity.noDate',
  later: 'activity.later',
};

const NEXT_STEP_TONE: Record<NextStepBucket, StageColumnTone> = {
  overdue: 'act_overdue',
  today: 'act_today',
  thisWeek: 'act_this_week',
  nextWeek: 'act_next_week',
  noDate: 'act_no_date',
  later: 'act_later',
};

const TRANSITION_TYPE_LABEL: Record<InquiryTransition['type'], TranslationKey> = {
  CREATED: 'transitions.typeCreated',
  ADVANCE: 'transitions.typeAdvance',
  SET_STAGE: 'transitions.typeSetStage',
  DROP: 'transitions.typeDrop',
  PAUSE: 'transitions.typePause',
  RESUME: 'transitions.typeResume',
  WIN: 'transitions.typeWin',
  SET_CONFIDENCE: 'transitions.typeSetConfidence',
  ASSIGN_MANAGER: 'transitions.typeAssignManager',
  CLIENT_ASSIGN: 'transitions.typeAssignClient',
  ATTACH: 'transitions.typeAttach',
  COMMENT: 'transitions.typeComment',
  SET_CRITERION: 'transitions.typeSetCriterion',
  ROLLBACK: 'transitions.typeRollback',
};

/** Issue #26 — map a basket suggestion onto the confidence scale to spot divergence. */
const CONFIDENCE_TO_BASKET: Record<Confidence, CriteriaBasket> = {
  K90: 'GREEN',
  K50: 'YELLOW',
  K10: 'RED',
};

const VIEW_STORAGE_KEY = 'rf-crm:inquiries-view';

interface Filters {
  search: string;
  stage: '' | Stage;
  confidence: '' | Confidence | 'none';
  integratorId: string;
  source: '' | InquirySource;
  type: '' | InquiryType;
  outcome: '' | 'WON' | 'LOST';
  /** Issue #73 — no «Все»: the tab is always set, industrial by default. */
  productType: ProductType;
  /** Issue #66 — responsible-manager filter (assignedManagerId). '' = все. */
  managerId: string;
}

const emptyFilters: Filters = {
  search: '',
  stage: '',
  confidence: '',
  integratorId: '',
  source: '',
  type: '',
  outcome: '',
  productType: 'INTEGRATION_PROJECT',
  managerId: '',
};

interface CreateInquiryForm {
  clientId: string;
  /** Issue #58 — the client INN, typed in the base form before the client field. */
  inn: string;
  source: string;
  channel: string;
  /** Issue #63 — optional card title (falls back to the client name when empty). */
  cardTitle: string;
  sourceEmail: string;
  summary: string;
  type: '' | InquiryType;
  /** Issue #59 — integrators picked on the form for the TKP (ТКП) calculation. */
  tkpIntegratorIds: string[];
}

const emptyForm: CreateInquiryForm = {
  clientId: '',
  inn: '',
  source: 'end_customer',
  channel: '',
  cardTitle: '',
  sourceEmail: '',
  summary: '',
  type: '',
  tkpIntegratorIds: [],
};

/**
 * Issue #63 — edit-in-place form for a created inquiry/lead. Same field set as
 * the create form plus the amount; the stage, history, outcome and the
 * remaining lifecycle fields stay out of reach (set from the actions menu).
 */
interface EditInquiryForm {
  cardTitle: string;
  source: string;
  channel: string;
  sourceEmail: string;
  type: '' | InquiryType;
  summary: string;
  /** Raw text so an empty box clears the amount ('' → null). */
  estimatedAmount: string;
  clientId: string;
  inn: string;
  tkpIntegratorIds: string[];
}

const emptyEditForm: EditInquiryForm = {
  cardTitle: '',
  source: 'end_customer',
  channel: '',
  sourceEmail: '',
  type: '',
  summary: '',
  estimatedAmount: '',
  clientId: '',
  inn: '',
  tkpIntegratorIds: [],
};

/**
 * Issue #61 — a bare Enter inside a create-form field must never submit the
 * form. HTML implicit submission fires the form's default button, so pressing
 * Enter in the INN / name / email field used to create the lead and close the
 * modal. TextArea keeps Enter (newlines) and buttons keep their own keyboard
 * activation — everything else has the default cancelled.
 */
function blockFormEnterSubmit(e: React.KeyboardEvent<HTMLFormElement>): void {
  if (e.key !== 'Enter') return;
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'TEXTAREA' || tag === 'BUTTON') return;
  e.preventDefault();
}

export function InquiriesPage() {
  const { user } = useAuth();
  const { t, locale } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isManufacturer = user?.role === 'MANUFACTURER';
  // Issue #66/#69 — «Менеджер» (User.isManager): sees the reduced filter set
  // (left group hidden, #66) but now the same full deal list as everyone else;
  // selecting themselves in the «Менеджер» picker narrows it to their own.
  const isManager = user?.isManager === true;
  // Issue #30 — integrators only see the handover stages; internal stages
  // (VALIDATION…REALIZATION) are neither filtered nor rendered for them.
  // CONTRACT joins the handover set with the industrial route (#80).
  const visibleStages: readonly Stage[] = isManufacturer
    ? STAGE_ORDER
    : ['INTEGRATOR_SELECTION', 'TKP', 'CONTRACT'];

  // ── View + filters ──────────────────────────────────────────────
  const [view, setView] = useState<InquiriesView>(() => {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === 'list' || stored === 'activity' || stored === 'calendar' ? stored : 'board';
  });
  useEffect(() => {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  // Issue #69 — every user, manager included, starts on «Все» in the manager
  // picker; a manager narrows to their own deals by picking themselves.
  const [filters, setFilters] = useState<Filters>(() => ({ ...emptyFilters }));
  const setFilter = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((prev) => ({ ...prev, [k]: v }));

  // Educational tab: the route skips PRIORITIZATION and INTEGRATOR_SELECTION,
  // so those columns (and «Отбой», below) are hidden — display-level only,
  // the stages themselves stay in the data. The stage filter above keeps the
  // full list on purpose (customer asked to change the board only).
  const boardStages: readonly Stage[] =
    filters.productType === 'EDUCATIONAL_ROBOT'
      ? visibleStages.filter((s) => EDUCATIONAL_ROUTE.includes(s))
      : visibleStages;

  // Debounce the search term so typing doesn't fire a request per keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => window.clearTimeout(id);
  }, [filters.search]);

  // ── Data ─────────────────────────────────────────────────────────
  const inquiriesQ = useQuery<Inquiry[]>({
    queryKey: ['inquiries', debouncedSearch, filters.stage, filters.confidence, filters.integratorId, filters.source, filters.type, filters.outcome, filters.productType, filters.managerId],
    queryFn: () =>
      api.get<Inquiry[]>('/inquiries', {
        query: {
          search: debouncedSearch || undefined,
          stage: filters.stage || undefined,
          confidence: filters.confidence || undefined,
          source: filters.source || undefined,
          type: filters.type || undefined,
          outcome: filters.outcome || undefined,
          assignedIntegratorId: filters.integratorId || undefined,
          assignedManagerId: filters.managerId || undefined,
          productType: filters.productType,
        },
      }),
  });
  const clientsQ = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: () => api.get<Client[]>('/clients'),
  });
  const integratorsQ = useQuery<Integrator[]>({
    queryKey: ['integrators'],
    queryFn: () => api.get<Integrator[]>('/integrators'),
    enabled: isManufacturer,
  });
  // Manager picklist for assignment (issue #23) — manufacturer only
  const managersQ = useQuery<User[]>({
    queryKey: ['users', 'managers'],
    queryFn: () => api.get<User[]>('/users', { query: { role: 'MANUFACTURER' } }),
    enabled: isManufacturer,
  });

  const inquiries = inquiriesQ.data ?? [];
  const hasActiveFilters =
    debouncedSearch !== '' ||
    filters.stage !== '' ||
    filters.confidence !== '' ||
    filters.integratorId !== '' ||
    filters.source !== '' ||
    filters.type !== '' ||
    filters.outcome !== '' ||
    filters.productType !== 'INTEGRATION_PROJECT' ||
    filters.managerId !== '';

  // ── Board grouping ───────────────────────────────────────────────
  const grouped = useMemo(() => {
    const byStage: Record<Stage, { active: Inquiry[]; paused: Inquiry[] }> = {
      VALIDATION: { active: [], paused: [] },
      VERIFICATION: { active: [], paused: [] },
      PRIORITIZATION: { active: [], paused: [] },
      REALIZATION: { active: [], paused: [] },
      INTEGRATOR_SELECTION: { active: [], paused: [] },
      TKP: { active: [], paused: [] },
      CONTRACT: { active: [], paused: [] },
    };
    const won: Inquiry[] = [];
    const lost: Inquiry[] = [];
    for (const inq of inquiries) {
      if (inq.outcome === 'WON') won.push(inq);
      else if (inq.outcome === 'LOST') lost.push(inq);
      else if (inq.paused) byStage[inq.stage].paused.push(inq);
      else byStage[inq.stage].active.push(inq);
    }
    return { byStage, won, lost };
  }, [inquiries]);

  const boardTotals = useMemo(() => {
    const open = inquiries.filter((i) => i.outcome === 'NONE');
    const amount = open.reduce((acc, i) => acc + (visibleInquiryAmount(i) ?? 0), 0);
    return { count: open.length, amount };
  }, [inquiries]);

  // ── Activity grouping («По делам», issue #52) ──────────────────
  const activityGrouped = useMemo(() => groupByNextStep(inquiries), [inquiries]);
  const overdueTotal = useMemo(() => overdueCount(inquiries), [inquiries]);

  // ── Detail drawer ────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = inquiries.find((i) => i.id === selectedId) ?? null;

  // Deep link from the notification bell: /inquiries?open=<id> (issue #32)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setSelectedId(openId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const transitionsQ = useQuery<InquiryTransition[]>({
    queryKey: ['inquiries', selectedId, 'transitions'],
    queryFn: () => api.get<InquiryTransition[]>(`/inquiries/${selectedId}/transitions`),
    enabled: selectedId !== null,
  });

  // Issue #26 — criteria scores (shared cache with CriteriaSection; also used
  // for the basket-vs-confidence hint and human-readable history labels).
  const criteriaQ = useQuery<InquiryCriteriaResponse>({
    queryKey: criteriaQueryKey(selectedId ?? ''),
    queryFn: () => api.get<InquiryCriteriaResponse>(`/inquiries/${selectedId}/criteria`),
    enabled: selectedId !== null,
  });

  // Artifacts list (shared cache with AttachmentsSection) — the concept block
  // surfaces CONCEPT-kind files next to the summary text.
  const attachmentsQ = useQuery<InquiryAttachment[]>({
    queryKey: ['inquiries', selectedId, 'attachments'],
    queryFn: () => api.get<InquiryAttachment[]>(`/inquiries/${selectedId}/attachments`),
    enabled: selectedId !== null,
  });
  const conceptArtifacts = useMemo(
    () => (attachmentsQ.data ?? []).filter((a) => a.kind === 'CONCEPT'),
    [attachmentsQ.data],
  );
  // Issue #76 — the «ТКП» sign in the card header shows ONLY when a ТКП
  // artifact is actually uploaded on the deal.
  const hasTkpArtifact = useMemo(
    () => (attachmentsQ.data ?? []).some((a) => a.kind === 'TKP'),
    [attachmentsQ.data],
  );

  // Client-approval flag on the concept — toggled in place from the drawer.
  const conceptApprovalMutation = useMutation({
    mutationFn: (approved: boolean) =>
      api.patch<Inquiry>(`/inquiries/${selectedId}`, { conceptApprovedByClient: approved }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('inquiries.toastUpdateFail'), msg);
    },
  });
  const criteriaLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of criteriaQ.data?.criteria ?? []) {
      map.set(c.key, locale === 'ru' ? c.labelRu : c.labelEn);
    }
    return map;
  }, [criteriaQ.data, locale]);

  // ── «Генподрядчик» (issue #78) ───────────────────────────────────
  // The block exists from the concept («Концепт/ТКП») step on. Educational
  // deals show the auto-assigned value read-only; industrial deals are edited
  // by the manufacturer — free text OR an integrator pick. The draft resets
  // per card (another lead opening), not on background refetches.
  const [gcDraft, setGcDraft] = useState('');
  useEffect(() => {
    setGcDraft(inquiries.find((i) => i.id === selectedId)?.generalContractor ?? '');
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps
  const generalContractorMutation = useMutation({
    mutationFn: (value: string | null) =>
      api.patch<Inquiry>(`/inquiries/${selectedId}`, { generalContractor: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      toast.success(t('inquiries.toastUpdated'));
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('inquiries.toastUpdateFail'), msg);
    },
  });

  // ── Comments in history (issue #44) ────────────────────────────
  const [commentTitle, setCommentTitle] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  // Issue #79 — «История» collapses to its header line by default; the open
  // state is tracked per lead id, so switching the card collapses it again.
  const [historyOpenFor, setHistoryOpenFor] = useState<{ id: string; open: boolean } | null>(null);
  const historyExpanded = historyOpenFor?.id === selectedId ? historyOpenFor.open : false;
  const [confidenceModalOpen, setConfidenceModalOpen] = useState(false);
  const commentMutation = useMutation({
    mutationFn: ({ id, title, text }: { id: string; title: string; text: string }) =>
      api.post<InquiryTransition>(`/inquiries/${id}/comments`, { title, text }),
    onSuccess: (_created, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries', id, 'transitions'] });
      toast.success(t('comments.toastAdded'));
      setCommentTitle('');
      setCommentText('');
      setCommentModalOpen(false);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('comments.toastFailed');
      toast.error(t('comments.toastFailed'), msg);
    },
  });

  const submitComment = () => {
    if (!selectedId || commentMutation.isPending) return;
    if (!commentTitle.trim() || !commentText.trim()) return;
    commentMutation.mutate({ id: selectedId, title: commentTitle, text: commentText });
  };

  // Issue #43 — download an artifact straight from its ATTACH history entry
  const downloadAttachment = async (attachmentId: string) => {
    if (!selectedId) return;
    try {
      const { blob, filename } = await apiDownload(
        `/inquiries/${selectedId}/attachments/${attachmentId}/download`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('attachments.toastFailed'), msg);
    }
  };

  // ── Transitions ──────────────────────────────────────────────────
  const transitionMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: TransitionInput }) =>
      api.post<Inquiry>(`/inquiries/${id}/transitions`, input),
    onSuccess: (_updated, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      void queryClient.invalidateQueries({ queryKey: ['inquiries', id, 'transitions'] });
      toast.success(t('inquiries.toastTransition'));
    },
  });

  const handleTransition = async (input: TransitionInput): Promise<void> => {
    if (!selectedId) return;
    // Soft rule (#23): advancing past VALIDATION without a manager — warn, don't block
    if (
      selected &&
      input.type === 'ADVANCE' &&
      selected.stage === 'VALIDATION' &&
      !selected.assignedManagerId &&
      !window.confirm(t('inquiries.warnNoManager'))
    ) {
      return;
    }
    try {
      await transitionMutation.mutateAsync({ id: selectedId, input });
    } catch (err) {
      // Re-throw so TransitionMenu shows the message inside its modal;
      // a toast mirrors it for actions that had no modal open.
      const msg = err instanceof ApiError ? err.message : t('inquiries.toastTransitionFail');
      toast.error(t('inquiries.toastTransitionFail'), msg);
      throw err;
    }
  };

  // ── Board drag & drop ──────────────────────────────────────────
  // HTML5 drag events: desktop enhancement only (they don't fire on
  // touch, so the horizontal column scroll on mobile stays intact).
  // The TransitionMenu in the drawer remains the accessible path.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dndDialog, setDndDialog] = useState<
    { inquiry: Inquiry; action: 'ADVANCE' | 'DROP' | 'WIN'; toStage?: Stage } | null
  >(null);
  const dragged = dragId ? (inquiries.find((i) => i.id === dragId) ?? null) : null;

  // Optimistic move with rollback. List queries have the shape
  // ['inquiries', search, stage, confidence, integratorId, source, type] —
  // the predicate keeps the transitions-history queries out.
  const dndMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: DndTransitionInput }) =>
      api.post<Inquiry>(`/inquiries/${id}/transitions`, input),
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: ['inquiries'] });
      const isListQuery = (q: { queryKey: readonly unknown[] }) =>
        q.queryKey[0] === 'inquiries' && q.queryKey.length === 9;
      const snapshots = queryClient.getQueriesData<Inquiry[]>({
        queryKey: ['inquiries'],
        predicate: isListQuery,
      });
      const optimistic = (inq: Inquiry): Inquiry => {
        if (input.type === 'ADVANCE') {
          const to = input.toStage ?? nextStageFor(inq.stage, inq.productType);
          return to
            ? {
                ...inq,
                stage: to,
                ...(input.confidence ? { confidence: input.confidence } : {}),
                ...(input.productType ? { productType: input.productType } : {}),
                ...(input.interactionMode ? { interactionMode: input.interactionMode } : {}),
                ...(input.auditAt ? { auditAt: input.auditAt } : {}),
                ...(input.conceptSummary ? { conceptSummary: input.conceptSummary } : {}),
                ...(input.conceptApprovedByClient !== undefined
                  ? { conceptApprovedByClient: input.conceptApprovedByClient }
                  : {}),
              }
            : inq;
        }
        if (input.type === 'WIN') return { ...inq, outcome: 'WON' as const };
        return { ...inq, outcome: 'LOST' as const };
      };
      queryClient.setQueriesData<Inquiry[]>(
        { queryKey: ['inquiries'], predicate: isListQuery },
        (old) => old?.map((i) => (i.id === id ? optimistic(i) : i)),
      );
      return { snapshots };
    },
    onError: (err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
      const msg = err instanceof ApiError ? err.message : t('inquiries.toastTransitionFail');
      toast.error(t('inquiries.toastTransitionFail'), msg);
    },
    onSuccess: () => {
      toast.success(t('inquiries.toastTransition'));
    },
    onSettled: () => {
      // Headers (count + ₽ sum) derive from the query — refetch settles them.
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });

  const handleCardDragStart = (inq: Inquiry, e: React.DragEvent<HTMLElement>) => {
    e.dataTransfer.setData('text/plain', inq.id);
    e.dataTransfer.effectAllowed = 'move';
    setDragId(inq.id);
  };
  const handleCardDragEnd = () => setDragId(null);

  const columnDropState = (key: BoardColumnKey): 'valid' | 'invalid' | undefined => {
    if (!dragged) return undefined;
    const r = resolveDrop(dragged, key, isManufacturer);
    return r.kind === 'submit' || r.kind === 'dialog' ? 'valid' : 'invalid';
  };

  const handleDropCard = (column: BoardColumnKey) => (inquiryId: string) => {
    setDragId(null);
    const inq = inquiries.find((i) => i.id === inquiryId);
    if (!inq) return;
    const r = resolveDrop(inq, column, isManufacturer);
    switch (r.kind) {
      case 'ignore':
        return;
      case 'invalid':
        toast.error(t('inquiries.toastTransitionFail'), t(r.reasonKey));
        return;
      case 'submit':
        // Soft rule (#23): warn when advancing past VALIDATION without a manager
        if (
          r.input.type === 'ADVANCE' &&
          inq.stage === 'VALIDATION' &&
          !inq.assignedManagerId &&
          !window.confirm(t('inquiries.warnNoManager'))
        ) {
          return;
        }
        dndMutation.mutate({ id: inq.id, input: r.input });
        return;
      case 'dialog':
        if (
          r.action === 'ADVANCE' &&
          inq.stage === 'VALIDATION' &&
          !inq.assignedManagerId &&
          !window.confirm(t('inquiries.warnNoManager'))
        ) {
          return;
        }
        setDndDialog({ inquiry: inq, action: r.action, toStage: r.toStage });
        return;
    }
  };

  const submitDndDialog = async (input: DndTransitionInput): Promise<void> => {
    if (!dndDialog) return;
    // Reject on failure so the dialog shows the server message inline;
    // the mutation's onError also rolls back + toasts.
    await dndMutation.mutateAsync({ id: dndDialog.inquiry.id, input });
  };

  // ── Manager assignment / take into work (issue #23) ────────────
  // Issue #52 — next-step date editor in the drawer (manufacturer only)
  const nextStepMutation = useMutation({
    mutationFn: ({ id, nextStepAt }: { id: string; nextStepAt: string | null }) =>
      api.patch<Inquiry>(`/inquiries/${id}`, { nextStepAt }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      toast.success(t('inquiries.toastTransition'));
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('inquiries.toastUpdateFail');
      toast.error(t('inquiries.toastUpdateFail'), msg);
    },
  });

  const assignManagerMutation = useMutation({
    mutationFn: ({ id, managerId }: { id: string; managerId: string | null }) =>
      api.patch<Inquiry>(`/inquiries/${id}`, { assignedManagerId: managerId }),
    onSuccess: (_updated, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      void queryClient.invalidateQueries({ queryKey: ['inquiries', id, 'transitions'] });
      toast.success(t('inquiries.toastTransition'));
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('inquiries.toastUpdateFail');
      toast.error(t('inquiries.toastUpdateFail'), msg);
    },
  });

  // Manual client assignment for no-client deals (email ingest mostly)
  const assignClientMutation = useMutation({
    mutationFn: ({ id, clientId }: { id: string; clientId: string | null }) =>
      api.patch<Inquiry>(`/inquiries/${id}`, { clientId }),
    onSuccess: (_updated, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      void queryClient.invalidateQueries({ queryKey: ['inquiries', id, 'transitions'] });
      toast.success(t('inquiries.toastTransition'));
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('inquiries.toastUpdateFail');
      toast.error(t('inquiries.toastUpdateFail'), msg);
    },
  });

  const takeMutation = useMutation({
    mutationFn: (id: string) => api.post<Inquiry>(`/inquiries/${id}/take`, {}),
    onSuccess: (_updated, id) => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      void queryClient.invalidateQueries({ queryKey: ['inquiries', id, 'transitions'] });
      toast.success(t('inquiries.toastTaken'));
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('inquiries.toastTakeFail');
      toast.error(t('inquiries.toastTakeFail'), msg);
    },
  });

  const handleTake = (inq: Inquiry) => {
    if (takeMutation.isPending) return;
    takeMutation.mutate(inq.id);
  };

  // ── List view sorting ────────────────────────────────────────────
  const [sort, setSort] = useState<{ key: 'client' | 'summary' | 'amount' | 'created' | 'age'; dir: 1 | -1 }>({
    key: 'created',
    dir: -1,
  });

  const sorted = useMemo(() => {
    const arr = [...inquiries];
    const val = (i: Inquiry): string | number => {
      switch (sort.key) {
        case 'client':
          return (i.client?.companyName ?? '').toLowerCase();
        case 'summary':
          return (i.summary ?? '').toLowerCase();
        case 'amount':
          return visibleInquiryAmount(i) ?? -1;
        case 'created':
        case 'age':
          return new Date(i.createdAt).getTime();
      }
    };
    arr.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : va - (vb as number);
      return cmp * sort.dir;
    });
    return arr;
  }, [inquiries, sort]);

  const toggleSort = (key: typeof sort.key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: key === 'client' || key === 'summary' ? 1 : -1 }));

  // ── Create flow ──────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateInquiryForm>(emptyForm);
  const [emailError, setEmailError] = useState<string | null>(null);
  const setField = <K extends keyof CreateInquiryForm>(k: K, v: CreateInquiryForm[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  // Issue #61/#62 — the lead's client email mirrors the linked client: it is
  // pre-filled from that client's record (the company name is shown by the
  // client chip) and re-filled whenever the link moves to another client (an
  // INN change relinks — see ClientTypeahead). An email the operator typed by
  // hand is never overwritten; when the link is dropped the auto-filled email
  // is cleared again so the form is clean.
  const prefilledEmailRef = useRef('');
  useEffect(() => {
    const linked = form.clientId
      ? (clientsQ.data ?? []).find((c) => c.id === form.clientId)
      : null;
    const email = linked?.contactEmail?.trim() ?? '';
    const current = form.sourceEmail.trim();
    if (current && current !== prefilledEmailRef.current) return; // operator's own email
    prefilledEmailRef.current = email;
    if (form.sourceEmail !== email) {
      setForm((prev) => ({ ...prev, sourceEmail: email }));
    }
  }, [form.clientId, clientsQ.data, form.sourceEmail]);

  // Issue #59 — the «Назначить Интегратора на расчёт ТКП» block is only
  // relevant for an industrial-robot lead whose «Тип запроса» is «Направлено
  // ТЗ» (REQUEST). A new lead inherits the product line of the active board
  // tab; «Все»/industrial tabs map to an integration project.
  const createProductType: ProductType =
    filters.productType === 'EDUCATIONAL_ROBOT' ? 'EDUCATIONAL_ROBOT' : 'INTEGRATION_PROJECT';
  const showsTkpIntegrators =
    createProductType === 'INTEGRATION_PROJECT' && form.type === 'REQUEST';

  const createMutation = useMutation({
    mutationFn: (payload: CreateInquiryForm) => {
      const body: Record<string, unknown> = {
        source: payload.source,
        summary: payload.summary.trim(),
      };
      if (payload.clientId) body.clientId = payload.clientId;
      if (payload.channel.trim()) body.channel = payload.channel.trim();
      // Issue #63 — optional card title
      if (payload.cardTitle.trim()) body.cardTitle = payload.cardTitle.trim();
      // Issue #47 — optional client email, stored on the inquiry itself
      if (payload.sourceEmail.trim()) body.sourceEmail = payload.sourceEmail.trim();
      if (payload.type) body.type = payload.type;
      // Issue #59 — the lead keeps its product type and the integrators
      // picked for the TKP (ТКП) calculation (multi-select).
      body.productType = createProductType;
      if (payload.tkpIntegratorIds.length > 0) body.tkpIntegratorIds = payload.tkpIntegratorIds;
      return api.post<Inquiry>('/inquiries', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      toast.success(t('inquiries.toastCreated'));
      setCreateOpen(false);
      setForm(emptyForm);
      setEmailError(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('inquiries.errorCreate');
      toast.error(t('inquiries.toastCreateFail'), msg);
    },
  });

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (createMutation.isPending) return;
    // Issue #47 — client-side format check before hitting the API
    const email = form.sourceEmail.trim();
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      setEmailError(t('inquiries.clientEmailInvalid'));
      return;
    }
    setEmailError(null);
    createMutation.mutate(form);
  };

  const errorMsg = inquiriesQ.error instanceof Error ? inquiriesQ.error.message : null;
  // Issue #61 — a failed create is surfaced inline in the form (not only as a
  // toast), so the modal stays usable and the operator can retry.
  const createErrorMsg =
    createMutation.error instanceof ApiError
      ? createMutation.error.message
      : createMutation.error
        ? t('inquiries.errorCreate')
        : null;

  // ── Edit an existing inquiry/lead (issue #63) ──────────────────
  // The stage and the transition history are untouched — only the card data.
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditInquiryForm>(emptyEditForm);
  // Client-side validation (email / amount) surfaced inline — same pattern as
  // the create form (issue #61: a rejected save never crashes the window).
  const [editEmailError, setEditEmailError] = useState<string | null>(null);
  const [editAmountError, setEditAmountError] = useState<string | null>(null);
  const setEditField = <K extends keyof EditInquiryForm>(k: K, v: EditInquiryForm[K]) =>
    setEditForm((prev) => ({ ...prev, [k]: v }));

  const editMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch<Inquiry>(`/inquiries/${id}`, body),
    onSuccess: (_updated, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      void queryClient.invalidateQueries({ queryKey: ['inquiries', id, 'transitions'] });
      toast.success(t('inquiries.toastUpdated'));
      setEditOpen(false);
    },
    onError: (err) => {
      // Errors stay in the form (see editErrorMsg) plus a toast mirror.
      const msg = err instanceof ApiError ? err.message : t('inquiries.errorEdit');
      toast.error(t('inquiries.toastEditFail'), msg);
    },
  });

  const openEdit = (inq: Inquiry) => {
    const amount = inquiryAmount(inq);
    editMutation.reset();
    setEditForm({
      cardTitle: inq.cardTitle ?? '',
      source: inq.source,
      channel: inq.channel ?? '',
      sourceEmail: inq.sourceEmail ?? '',
      type: inq.type ?? '',
      summary: inq.summary ?? '',
      estimatedAmount: amount !== null ? String(amount) : '',
      clientId: inq.clientId ?? '',
      inn: '',
      tkpIntegratorIds: inq.tkpIntegratorIds ?? [],
    });
    setEditEmailError(null);
    setEditAmountError(null);
    setEditOpen(true);
  };

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId || editMutation.isPending) return;
    const email = editForm.sourceEmail.trim();
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      setEditEmailError(t('inquiries.clientEmailInvalid'));
      return;
    }
    setEditEmailError(null);
    const amountRaw = editForm.estimatedAmount.trim();
    let amount: number | null = null;
    if (amountRaw) {
      const n = Number(amountRaw.replace(/\s/g, '').replace(',', '.'));
      if (!Number.isFinite(n) || n < 0) {
        setEditAmountError(t('inquiries.amountInvalid'));
        return;
      }
      amount = n;
    }
    setEditAmountError(null);
    const body: Record<string, unknown> = {
      cardTitle: editForm.cardTitle.trim() || null,
      source: editForm.source,
      channel: editForm.channel.trim(),
      type: editForm.type || null,
      sourceEmail: email || null,
      summary: editForm.summary.trim(),
      clientId: editForm.clientId || null,
      estimatedAmount: amount,
    };
    // TKP integrators are only meaningful for an industrial «Направлено ТЗ»
    // lead — otherwise the stored list is left untouched.
    const editProductType: ProductType = selected?.productType ?? 'INTEGRATION_PROJECT';
    if (editProductType === 'INTEGRATION_PROJECT' && editForm.type === 'REQUEST') {
      body.tkpIntegratorIds = editForm.tkpIntegratorIds;
    }
    editMutation.mutate({ id: selectedId, body });
  };

  // TKP integrators are only meaningful for an industrial «Направлено ТЗ» lead
  const editShowsTkpIntegrators =
    (selected?.productType ?? 'INTEGRATION_PROJECT') === 'INTEGRATION_PROJECT' &&
    editForm.type === 'REQUEST';

  // Issue #61/#63 — a failed save is surfaced inline in the edit form too.
  const editErrorMsg =
    editMutation.error instanceof ApiError
      ? editMutation.error.message
      : editMutation.error
        ? t('inquiries.errorEdit')
        : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('inquiries.title')}
        description={isManufacturer ? t('inquiries.descAll') : t('inquiries.descOwn')}
        action={
          <Button
            leftIcon={<Plus size={16} />}
            onClick={() => {
              createMutation.reset();
              setCreateOpen(true);
            }}
          >
            {t('inquiries.new')}
          </Button>
        }
      />

      {/* ── Product tabs — the board's headline choice (issue #67) ────────────
          Lifted out of the toolbar into its own row right under the page
          header and grouped into one framed block, so the tabs read as
          «start here» rather than as one more filter next to the selects.
          Issue #73 — no «Все» tab: industrial and educational robots are
          never mixed; industrial is the default. */}
      <div className={styles.productBar}>
        <div
          className={styles.productTabs}
          role="tablist"
          aria-label={t('transitions.productTypeLabel')}
        >
          {(['INTEGRATION_PROJECT', 'EDUCATIONAL_ROBOT'] as const).map((pt) => (
            <Button
              key={pt}
              size="sm"
              variant={filters.productType === pt ? 'primary' : 'ghost'}
              onClick={() => setFilter('productType', pt)}
            >
              {pt === 'INTEGRATION_PROJECT'
                ? t('transitions.productTypeIntegration')
                : t('transitions.productTypeEducational')}
            </Button>
          ))}
        </div>
      </div>

      {/* ── Subbar: view toggle + search + filters ──────────────────────────── */}
      <div className={styles.subbar}>
        <ViewToggle value={view} onChange={setView} overdueBadge={overdueTotal} />
        <div className={styles.search}>
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            placeholder={t('inquiries.searchPlaceholder')}
            aria-label={t('inquiries.searchAria')}
          />
        </div>
        {isManufacturer && (
          <Select
            aria-label={t('inquiries.filterManager')}
            value={filters.managerId}
            onChange={(e) => setFilter('managerId', e.target.value)}
            options={[
              { value: '', label: `${t('inquiries.filterManager')}: ${t('inquiries.allOption')}` },
              ...(managersQ.data ?? []).map((u) => ({
                value: u.id,
                label: `${u.name} (${u.email})`,
              })),
            ]}
          />
        )}
        {/* Issue #66 — the left filter group (stage / outcome / confidence /
            integrator / source / type) is redundant for «Менеджер»: he works his
            own deals. «Руководитель» keeps the full set. */}
        {!isManager && (
          <>
            <Select
              aria-label={t('inquiries.filterStage')}
              value={filters.stage}
              onChange={(e) => setFilter('stage', e.target.value as Filters['stage'])}
              options={[
                { value: '', label: `${t('inquiries.filterStage')}: ${t('inquiries.allOption')}` },
                ...visibleStages.map((s) => ({ value: s, label: t(STAGE_LABEL_KEY[s]) })),
              ]}
            />
            <Select
              aria-label={t('inquiries.filterOutcome')}
              value={filters.outcome}
              onChange={(e) => setFilter('outcome', e.target.value as Filters['outcome'])}
              options={[
                { value: '', label: `${t('inquiries.filterOutcome')}: ${t('inquiries.allOption')}` },
                { value: 'WON', label: t('stage.won') },
                { value: 'LOST', label: t('stage.lost') },
              ]}
            />
            <Select
              aria-label={t('inquiries.filterConfidence')}
              value={filters.confidence}
              onChange={(e) => setFilter('confidence', e.target.value as Filters['confidence'])}
              options={[
                { value: '', label: `${t('inquiries.filterConfidence')}: ${t('inquiries.allOption')}` },
                { value: 'K90', label: t('confidence.k90') },
                { value: 'K50', label: t('confidence.k50') },
                { value: 'K10', label: t('confidence.k10') },
                { value: 'none', label: t('confidence.unset') },
              ]}
            />
            {isManufacturer && (
              <Select
                aria-label={t('inquiries.filterIntegrator')}
                value={filters.integratorId}
                onChange={(e) => setFilter('integratorId', e.target.value)}
                options={[
                  { value: '', label: `${t('inquiries.filterIntegrator')}: ${t('inquiries.allOption')}` },
                  ...(integratorsQ.data ?? []).map((i) => ({ value: i.id, label: i.name })),
                ]}
              />
            )}
            <Select
              aria-label={t('inquiries.filterSource')}
              value={filters.source}
              onChange={(e) => setFilter('source', e.target.value as Filters['source'])}
              options={[
                { value: '', label: `${t('inquiries.filterSource')}: ${t('inquiries.allOption')}` },
                ...SOURCES.map((s) => ({ value: s.value, label: t(s.labelKey) })),
              ]}
            />
            <Select
              aria-label={t('inquiries.filterType')}
              value={filters.type}
              onChange={(e) => setFilter('type', e.target.value as Filters['type'])}
              options={[
                { value: '', label: `${t('inquiries.filterType')}: ${t('inquiries.allOption')}` },
                ...INQUIRY_TYPES.map((v) => ({ value: v, label: t(TYPE_LABEL_KEY[v]) })),
              ]}
            />
          </>
        )}
      </div>

      {errorMsg && (
        <Card className={styles.error}>
          <p>{errorMsg}</p>
        </Card>
      )}

      {inquiriesQ.isLoading ? (
        view === 'list' ? <ListSkeleton /> : <BoardSkeleton />
      ) : inquiries.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Inbox size={28} />}
            title={hasActiveFilters ? t('inquiries.emptyFilteredTitle') : t('inquiries.emptyTitle')}
            description={hasActiveFilters ? t('inquiries.emptyFilteredDesc') : t('inquiries.emptyDesc')}
            action={
              !hasActiveFilters && (
                <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
                  {t('inquiries.new')}
                </Button>
              )
            }
          />
        </Card>
      ) : view === 'activity' ? (
        // Issue #52 — activity grouping («По делам»): columns by next-step date.
        // DnD stays off here — columns aren't stages, dragging would be a no-op.
        <div className={styles.board}>
          {NEXT_STEP_BUCKETS.map((bucket) => (
            <StageColumn
              key={bucket}
              tone={NEXT_STEP_TONE[bucket]}
              title={t(NEXT_STEP_LABEL_KEY[bucket])}
              items={activityGrouped[bucket]}
              onOpen={(inq) => setSelectedId(inq.id)}
              canTake={isManufacturer}
              onTake={handleTake}
            />
          ))}
        </div>
      ) : view === 'calendar' ? (
        // Issue #53 — calendar view: deals laid out by next-step date.
        <InquiriesCalendar items={inquiries} onOpen={(inq) => setSelectedId(inq.id)} />
      ) : view === 'board' ? (
        <>
          <div className={dragId ? `${styles.board} ${styles.boardDragging}` : styles.board}>
            {boardStages.map((stage) => (
              <StageColumn
                key={stage}
                tone={STAGE_TONE[stage]}
                title={t(stageLabelKey(stage, filters.productType))}
                items={grouped.byStage[stage].active}
                pausedItems={grouped.byStage[stage].paused}
                onOpen={(inq) => setSelectedId(inq.id)}
                dropState={columnDropState(stage)}
                onDropCard={handleDropCard(stage)}
                cardDraggable={isManufacturer}
                draggingId={dragId}
                onCardDragStart={handleCardDragStart}
                onCardDragEnd={handleCardDragEnd}
                canTake={isManufacturer}
                onTake={handleTake}
              />
            ))}
            {/* «Выиграно» убрана с активной доски по запросу заказчика —
                колонки шире, заголовки стадий читаются целиком. Выигранные
                сделки доступны через фильтр «Исход: Выиграно» (тогда колонка
                показывается снова) или в виде списка. */}
            {filters.outcome === 'WON' && (
              <StageColumn
                tone="won"
                title={t('stage.won')}
                items={grouped.won}
                onOpen={(inq) => setSelectedId(inq.id)}
                dropState={columnDropState('WON')}
                onDropCard={handleDropCard('WON')}
                cardDraggable={isManufacturer}
                draggingId={dragId}
                onCardDragStart={handleCardDragStart}
                onCardDragEnd={handleCardDragEnd}
                canTake={isManufacturer}
                onTake={handleTake}
              />
            )}
            {/* Educational tab hides «Отбой» together with the skipped
                route steps — the customer tracks educational losses offline. */}
            {filters.productType !== 'EDUCATIONAL_ROBOT' && (
              <StageColumn
                tone="lost"
                title={t('stage.lost')}
                items={grouped.lost}
                muted
                onOpen={(inq) => setSelectedId(inq.id)}
                dropState={columnDropState('LOST')}
                onDropCard={handleDropCard('LOST')}
                cardDraggable={isManufacturer}
                draggingId={dragId}
                onCardDragStart={handleCardDragStart}
                onCardDragEnd={handleCardDragEnd}
                canTake={isManufacturer}
                onTake={handleTake}
              />
            )}
          </div>
          <footer className={styles.boardFoot}>
            <span>
              {t('inquiries.boardFootTotal', { count: boardTotals.count })}
            </span>
            {boardTotals.amount > 0 && (
              <span className={styles.mono}>
                {t('inquiries.boardFootAmount', { amount: formatMoney(boardTotals.amount, locale) })}
              </span>
            )}
          </footer>
        </>
      ) : (
        <ListView
          items={sorted}
          sort={sort}
          onSort={toggleSort}
          onOpen={(inq) => setSelectedId(inq.id)}
        />
      )}

      {/* ── Detail drawer ──────────────────────────────────────── */}
      <Drawer
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        title={
          selected ? (
            // Issue #63 — the card header is the deal's own title when set,
            // otherwise the client name (previous behaviour).
            inquiryTitle(selected) ? (
              <span>{inquiryTitle(selected)}</span>
            ) : (
              <EntityLink
                kind="client"
                id={selected.client?.id ?? selected.clientId}
                name={selected.client?.companyName}
                fallback={t('common.noClient')}
              />
            )
          ) : (
            inquiryRef({ id: selectedId ?? '' })
          )
        }
        description={selected ? inquiryRef(selected) : undefined}
        size="lg"
      >
        {selected && (
          <div className={styles.drawerBody}>
            <section aria-label={t('inquiries.drawerDetailsTitle')} className={styles.details}>
              <div className={styles.sectionHead}>
                <h3 className={styles.sectionTitle}>{t('inquiries.drawerDetailsTitle')}</h3>
                <div className={styles.sectionHeadActions}>
                  {/* Issue #63 — edit the card data of a created inquiry */}
                  {isManufacturer && (
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Pencil size={14} />}
                      onClick={() => openEdit(selected)}
                    >
                      {t('inquiries.edit')}
                    </Button>
                  )}
                  {isManufacturer && !selected.assignedManagerId && selected.outcome === 'NONE' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={takeMutation.isPending}
                      onClick={() => handleTake(selected)}
                    >
                      {t('inquiries.takeIntoWork')}
                    </Button>
                  )}
                </div>
              </div>
              <p className={styles.drawerSummary}>{selected.summary}</p>
              {/* Issue #41 — meta row: status, confidence, type and source sit
                  on one wrapping row, so a long stage name never overlaps the
                  confidence badge; each chip wraps to its own line on narrow. */}
              <div className={styles.metaRow}>
                <span className={styles.metaItem}>
                  <StatusPill
                    tone={
                      selected.outcome === 'WON'
                        ? 'outcome_won'
                        : selected.outcome === 'LOST'
                          ? 'outcome_lost'
                          : STAGE_PILL_TONE[selected.stage]
                    }
                    className={styles.metaPill}
                  >
                    {selected.outcome === 'WON'
                      ? t('stage.won')
                      : selected.outcome === 'LOST'
                        ? t('stage.lost')
                        : t(STAGE_LABEL_KEY[selected.stage])}
                  </StatusPill>
                  {selected.paused && (
                    <Pause size={13} className={styles.pausedMark} aria-label={t('stage.paused')} />
                  )}
                </span>
                {/* Issue #76 — the «ТКП» sign shows only when a ТКП artifact is
                    uploaded on the deal; no artifact — no sign. */}
                {hasTkpArtifact && (
                  <span className={styles.metaItem} title={t('inquiries.tkpArtifactHint')}>
                    <StatusPill tone="success" className={styles.metaPill}>
                      {t('attachments.kindTkp')}
                    </StatusPill>
                  </span>
                )}
                <span className={styles.metaItem}>
                  <ConfidenceControl inquiry={selected} onEdit={() => setConfidenceModalOpen(true)} />
                  {/* Issue #26 — subtle basket-vs-confidence hint. Suggestion
                      only: never auto-applies, and stays hidden before
                      PRIORITIZATION where confidence is locked (issue #42). */}
                  {(() => {
                    const basket = criteriaQ.data?.aggregate.basket;
                    if (!basket) return null;
                    if (!CONFIDENCE_REQUIRED_STAGES.includes(selected.stage)) return null;
                    if (selected.confidence && CONFIDENCE_TO_BASKET[selected.confidence] === basket) {
                      return null;
                    }
                    return (
                      <span className={styles.criteriaHint}>
                        {t('criteria.confidenceHint', { basket: t(BASKET_LABEL_KEY[basket]) })}
                      </span>
                    );
                  })()}
                </span>
                {isManufacturer && selected.type && (
                  <span className={styles.metaItem}>
                    <TypeChip type={selected.type} />
                  </span>
                )}
                {/* Issue #77 — the source chip («Сотрудник ЗР» etc.) removed
                    from the card header: zero information load (client,
                    16.10). The list view keeps its «Источник» column. */}
              </div>
              <dl className={styles.detailGrid}>
                {/* Issue #77 — the raw «Канал» service value removed from the
                    card details (client, 16.10); channel stays editable in
                    the create/edit form. */}
                {inquiryAmount(selected) !== null && (
                  <div>
                    <dt>{t('inquiries.fieldAmount')}</dt>
                    <dd className={styles.mono}>{formatMoney(inquiryAmount(selected)!, locale)}</dd>
                  </div>
                )}
                {selected.productType && (
                  <div>
                    <dt>{t('transitions.productTypeLabel')}</dt>
                    <dd>
                      {t(
                        selected.productType === 'EDUCATIONAL_ROBOT'
                          ? 'transitions.productTypeEducational'
                          : 'transitions.productTypeIntegration',
                      )}
                    </dd>
                  </div>
                )}
                {selected.client?.contactEmail && (
                  <div>
                    <dt>{t('inquiries.fieldClientEmail')}</dt>
                    <dd>{selected.client.contactEmail}</dd>
                  </div>
                )}
                {/* Issue #47 — email captured on the inquiry itself */}
                {selected.sourceEmail && (
                  <div>
                    <dt>{t('inquiries.fieldSourceEmail')}</dt>
                    <dd>{selected.sourceEmail}</dd>
                  </div>
                )}
                <div>
                  <dt>{t('inquiries.fieldCreated')}</dt>
                  <dd className={styles.mono}>{formatDateTime(selected.createdAt, locale)}</dd>
                </div>
                {/* Issue #52 — next-step date: inline editor for the
                    manufacturer, read-only date for integrators. */}
                {isManufacturer ? (
                  <div>
                    <dt>{t('inquiries.fieldNextStep')}</dt>
                    <dd className={styles.nextStepEdit}>
                      <input
                        type="date"
                        className={styles.nextStepInput}
                        aria-label={t('inquiries.fieldNextStep')}
                        value={selected.nextStepAt ? selected.nextStepAt.slice(0, 10) : ''}
                        disabled={nextStepMutation.isPending}
                        onChange={(e) =>
                          nextStepMutation.mutate({
                            id: selected.id,
                            nextStepAt: e.target.value || null,
                          })
                        }
                      />
                      {selected.nextStepAt && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label={t('inquiries.nextStepClear')}
                          leftIcon={<X size={14} />}
                          disabled={nextStepMutation.isPending}
                          onClick={() =>
                            nextStepMutation.mutate({ id: selected.id, nextStepAt: null })
                          }
                        />
                      )}
                    </dd>
                  </div>
                ) : (
                  selected.nextStepAt && (
                    <div>
                      <dt>{t('inquiries.fieldNextStep')}</dt>
                      <dd className={styles.mono}>{formatDate(selected.nextStepAt, locale)}</dd>
                    </div>
                  )
                )}
              </dl>
              {/* Manual client assignment — no-client deals are mostly email
                  ingest: give the manager a way to link or create a client. */}
              {isManufacturer && selected.clientId === null && (
                <ClientTypeahead
                  clients={clientsQ.data ?? []}
                  value={selected.clientId ?? ''}
                  onChange={(id) =>
                    assignClientMutation.mutate({ id: selected.id, clientId: id || null })
                  }
                  isManufacturer={isManufacturer}
                  label={t('inquiries.assignClient')}
                  helper={t('inquiries.assignClientHelper')}
                />
              )}
              {/* Issue #54 — client contacts (roles, primary flag), read-only. */}
              {selected.client?.id && (
                <ClientContacts clientId={selected.client.id} readOnly />
              )}
              {selected.paused && selected.pauseReason && (
                <p className={styles.reasonNote}>
                  <strong>{t('inquiries.pauseReasonLabel')}:</strong> {selected.pauseReason}
                </p>
              )}
              {selected.paused && selected.pauseRemindAt && (
                <p className={styles.reasonNote}>
                  <strong>{t('inquiries.pauseRemindLabel')}:</strong>{' '}
                  {formatDate(selected.pauseRemindAt, locale)}
                </p>
              )}
              {selected.confidenceReason && (
                <p className={styles.reasonNote}>
                  <strong>{t('inquiries.confidenceReasonLabel')}:</strong> {selected.confidenceReason}
                </p>
              )}
              {selected.outcomeReason && (
                <p className={styles.reasonNote}>
                  <strong>{t('inquiries.outcomeReasonLabel')}:</strong> {selected.outcomeReason}
                </p>
              )}
            </section>

            {/* Issue #79 — «Участники» is an industrial-deal block: the
                educational route skips integrator selection and manager
                assignment, so the section never renders there. */}
            {isManufacturer && selected.productType === 'INTEGRATION_PROJECT' && (
            <section aria-label={t('inquiries.drawerParticipantsTitle')}>
              <h3 className={styles.sectionTitle}>{t('inquiries.drawerParticipantsTitle')}</h3>
              <dl className={styles.detailGrid}>
                <div>
                  <dt>{t('inquiries.fieldIntegrator')}</dt>
                  <dd>
                    <EntityLink
                      kind="integrator"
                      id={selected.assignedIntegrator?.id ?? selected.assignedIntegratorId}
                      name={selected.assignedIntegrator?.name}
                      fallback={t('common.notAssigned')}
                    />
                  </dd>
                </div>
                <div>
                  <dt>{t('inquiries.fieldManager')}</dt>
                  <dd>
                    <Select
                      aria-label={t('inquiries.fieldManager')}
                      value={selected.assignedManagerId ?? ''}
                      onChange={(e) =>
                        assignManagerMutation.mutate({
                          id: selected.id,
                          managerId: e.target.value || null,
                        })
                      }
                      disabled={assignManagerMutation.isPending}
                      options={[
                        { value: '', label: t('common.notAssigned') },
                        ...(managersQ.data ?? []).map((u) => ({ value: u.id, label: u.name })),
                      ]}
                    />
                  </dd>
                </div>
                <div>
                  <dt>{t('inquiries.fieldCreatedBy')}</dt>
                  <dd>{selected.createdBy?.name ?? '—'}</dd>
                </div>
              </dl>
            </section>
            )}

            {/* Issue #78 — «Генподрядчик»: from the concept («Концепт/ТКП»)
                step on. Educational deals show the auto-assigned value
                read-only; industrial deals are edited by the manufacturer —
                free text OR a pick from the integrators list. */}
            {stageShowsGeneralContractor(selected.stage) && (
            <section aria-label={t('inquiries.generalContractorTitle')}>
              <h3 className={styles.sectionTitle}>{t('inquiries.generalContractorTitle')}</h3>
              {selected.productType === 'EDUCATIONAL_ROBOT' ? (
                <div className={styles.infoBlockBody}>
                  <span>{generalContractorDisplay(selected)}</span>
                  <span className={styles.infoBlockMeta}>
                    {t('inquiries.generalContractorAuto')}
                  </span>
                </div>
              ) : isManufacturer ? (
                <div className={styles.gcRow}>
                  <Input
                    aria-label={t('inquiries.generalContractorTitle')}
                    value={gcDraft}
                    onChange={(e) => setGcDraft(e.target.value)}
                    placeholder={t('inquiries.generalContractorPlaceholder')}
                    disabled={generalContractorMutation.isPending}
                    className={styles.gcInput}
                  />
                  <Select
                    aria-label={t('inquiries.generalContractorPick')}
                    value=""
                    onChange={(e) => {
                      if (e.target.value) setGcDraft(e.target.value);
                    }}
                    disabled={generalContractorMutation.isPending}
                    placeholder={t('inquiries.generalContractorPick')}
                    options={(integratorsQ.data ?? [])
                      .filter((i) => i.active)
                      .map((i) => ({ value: i.name, label: i.name }))}
                    className={styles.gcPick}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={
                      gcDraft.trim() === (selected.generalContractor ?? '').trim() ||
                      generalContractorMutation.isPending
                    }
                    loading={generalContractorMutation.isPending}
                    onClick={() =>
                      generalContractorMutation.mutate(gcDraft.trim() || null)
                    }
                  >
                    {t('common.save')}
                  </Button>
                </div>
              ) : (
                <div className={styles.infoBlockBody}>
                  <span>{selected.generalContractor ?? t('common.notAssigned')}</span>
                </div>
              )}
            </section>
            )}

            {/* Issue #80 (slice 2) — «Параметры сделки» lives ONLY on the
                «Контракт» step: industrial deals on CONTRACT, educational on
                TKP (their contract). The PATCH gate accepts writes from
                REALIZATION on, but the block surfaces only at the contract —
                a deliberate slice-2 decision recorded in the MR. */}
            {stageShowsDealParams(selected.stage, selected.productType) && (
            <DealParamsSection inquiry={selected} canEdit={isManufacturer} />
            )}

            <section aria-label={t('inquiries.drawerActionsTitle')} className={styles.actionsSection}>
              <h3 className={styles.sectionTitle}>{t('inquiries.drawerActionsTitle')}</h3>
              <TransitionMenu
                inquiry={selected}
                isManufacturer={isManufacturer}
                pending={transitionMutation.isPending}
                onAction={handleTransition}
                confidenceModalOpen={confidenceModalOpen}
                onConfidenceModalClose={() => setConfidenceModalOpen(false)}
              />
            </section>

            {selected.interactionMode && (
              <section aria-label={t('transitions.interactionModeLabel')} className={styles.infoBlock}>
                <h3 className={styles.sectionTitle}>{t('transitions.interactionModeLabel')}</h3>
                <div className={styles.infoBlockBody}>
                  <StatusPill tone="info">
                    {t(
                      selected.interactionMode === 'AUDIT'
                        ? 'transitions.modeAudit'
                        : 'transitions.modeRemoteTz',
                    )}
                  </StatusPill>
                  {selected.interactionMode === 'AUDIT' && selected.auditAt && (
                    <span className={styles.infoBlockMeta}>
                      {t('transitions.auditDateLabel')}: {formatDate(selected.auditAt, locale)}
                    </span>
                  )}
                </div>
              </section>
            )}

            {(selected.conceptSummary || conceptArtifacts.length > 0) && (
              <section aria-label={t('transitions.conceptTitle')} className={styles.infoBlock}>
                <div className={styles.sectionHeadStart}>
                  <h3 className={styles.sectionTitle}>{t('transitions.conceptTitle')}</h3>
                  {isManufacturer ? (
                    // Issue #72 — from the «ТКП» step on (TKP and the
                    // industrial CONTRACT after it) the concept is already
                    // behind the deal, the approval checkbox is noise.
                    selected.stage !== 'TKP' && selected.stage !== 'CONTRACT' && (
                      <Checkbox
                        size="sm"
                        label={t('transitions.conceptApprovedLabel')}
                        checked={selected.conceptApprovedByClient ?? false}
                        disabled={conceptApprovalMutation.isPending}
                        onChange={(e) => conceptApprovalMutation.mutate(e.target.checked)}
                      />
                    )
                  ) : (
                    <StatusPill tone={selected.conceptApprovedByClient ? 'success' : 'neutral'}>
                      {t(
                        selected.conceptApprovedByClient
                          ? 'transitions.conceptApprovedLabel'
                          : 'transitions.conceptNotApproved',
                      )}
                    </StatusPill>
                  )}
                </div>
                {selected.conceptSummary && (
                  <p className={styles.conceptText}>{selected.conceptSummary}</p>
                )}
                {conceptArtifacts.length > 0 && (
                  <ul className={styles.conceptFiles}>
                    {conceptArtifacts.map((a) => (
                      <li key={a.id}>{a.title || a.filename}</li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            <AttachmentsSection inquiryId={selected.id} canUpload={isManufacturer} />

            {/* Issue #79 — verification criteria are worked only on the
                «Верификация» step; on every other step the block is hidden. */}
            {selected.stage === 'VERIFICATION' && (
              <CriteriaSection
                inquiryId={selected.id}
                canEdit={isManufacturer}
                stage={selected.stage}
                outcome={selected.outcome}
              />
            )}

            <section aria-label={t('inquiries.timelineTitle')}>
              {/* Customer feedback — the always-expanded composer took too
                  much drawer space; it now lives behind a header button +
                  modal (same pattern as «Загрузить артефакт»).
                  Issue #79 — the timeline itself is collapsible too: collapsed
                  to the header line by default, click the title to expand. */}
              <div className={styles.sectionHeadStart}>
                <h3 className={styles.sectionTitle}>
                  <button
                    type="button"
                    className={styles.historyToggle}
                    aria-expanded={historyExpanded}
                    onClick={() =>
                      setHistoryOpenFor({ id: selected.id, open: !historyExpanded })
                    }
                  >
                    {t('inquiries.timelineTitle')}
                    <ChevronDown size={14} aria-hidden="true" />
                  </button>
                </h3>
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Plus size={14} />}
                  onClick={() => setCommentModalOpen(true)}
                >
                  {t('comments.addTitle')}
                </Button>
              </div>

              <Modal
                open={commentModalOpen}
                onClose={() => !commentMutation.isPending && setCommentModalOpen(false)}
                title={t('comments.addTitle')}
                size="md"
                footer={
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setCommentModalOpen(false)}
                      disabled={commentMutation.isPending}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={submitComment}
                      loading={commentMutation.isPending}
                      disabled={!commentTitle.trim() || !commentText.trim()}
                    >
                      {t('comments.submit')}
                    </Button>
                  </>
                }
              >
                <div className={styles.commentModalForm}>
                  <Input
                    label={t('comments.fieldTitle')}
                    value={commentTitle}
                    onChange={(e) => setCommentTitle(e.target.value)}
                    placeholder={t('comments.titlePlaceholder')}
                    fullWidth
                  />
                  <TextArea
                    label={t('comments.fieldText')}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={t('comments.textPlaceholder')}
                    rows={3}
                    fullWidth
                  />
                </div>
              </Modal>

              {historyExpanded && (
              <>
              {transitionsQ.isLoading ? (
                <div className={styles.timelineSkeleton}>
                  <Skeleton width="70%" />
                  <Skeleton width="55%" />
                  <Skeleton width="62%" />
                </div>
              ) : (transitionsQ.data ?? []).length === 0 ? (
                <p className={styles.timelineEmpty}>{t('inquiries.timelineEmpty')}</p>
              ) : (
                <ol className={styles.timeline}>
                  {[...(transitionsQ.data ?? [])].reverse().map((tr) => (
                    <li key={tr.id} className={styles.timelineItem}>
                      <div className={styles.timelineHead}>
                        <span className={styles.timelineType}>{t(TRANSITION_TYPE_LABEL[tr.type] ?? 'transitions.typeCreated')}</span>
                        {tr.fromStage && tr.toStage && tr.fromStage !== tr.toStage && (
                          <span className={styles.timelineStages}>
                            {t(STAGE_LABEL_KEY[tr.fromStage])} → {t(STAGE_LABEL_KEY[tr.toStage])}
                          </span>
                        )}
                        {/* Issue #65 — a rollback from «Отбой»/«Выигрыш» keeps the
                            stage (fromStage === toStage): the deal is revived
                            into the funnel rather than moved between stages. */}
                        {tr.type === 'ROLLBACK' && tr.fromStage && tr.toStage && tr.fromStage === tr.toStage && (
                          <span className={styles.timelineStages}>
                            {t('transitions.rollbackFunnelReturn', {
                              stage: t(STAGE_LABEL_KEY[tr.toStage]),
                            })}
                          </span>
                        )}
                        {tr.confidence && <ConfidenceBadge confidence={tr.confidence} />}
                        {/* Issue #43 — download the artifact from its history entry */}
                        {tr.type === 'ATTACH' && tr.attachmentId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label={t('attachments.download')}
                            leftIcon={<Download size={14} />}
                            onClick={() => void downloadAttachment(tr.attachmentId!)}
                          />
                        )}
                      </div>
                      {tr.reason && (
                        <p className={tr.type === 'COMMENT' ? styles.timelineCommentTitle : styles.timelineReason}>
                          {tr.type === 'SET_CRITERION'
                            ? t('criteria.historyLine', {
                                label: criteriaLabelByKey.get(tr.reason) ?? tr.reason,
                                score: tr.criterionScore ?? '—',
                              })
                            : tr.reason}
                        </p>
                      )}
                      {tr.note && <p className={styles.timelineNote}>{tr.note}</p>}
                      <span className={styles.timelineMeta}>
                        {tr.actor?.name ?? '—'} · {formatDateTime(tr.createdAt, locale)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              </>
              )}
            </section>
          </div>
        )}
      </Drawer>

      {/* ── DnD follow-up dialog (reason / confidence) ───────── */}
      {dndDialog && (
        <InquiriesDndDialog
          inquiry={dndDialog.inquiry}
          action={dndDialog.action}
          toStage={dndDialog.toStage}
          onSubmit={submitDndDialog}
          onClose={() => setDndDialog(null)}
        />
      )}

      {/* ── Edit Inquiry modal (issue #63) ───────────────────── */}
      <Modal
        open={editOpen && selected !== null}
        onClose={() => !editMutation.isPending && setEditOpen(false)}
        title={t('inquiries.editTitle')}
        description={inquiryRef(selected ?? { id: selectedId ?? '' })}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setEditOpen(false)}
              disabled={editMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="edit-inquiry-form" loading={editMutation.isPending}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <form
          id="edit-inquiry-form"
          onSubmit={submitEdit}
          onKeyDown={blockFormEnterSubmit}
          className={styles.form}
          noValidate
        >
          {editErrorMsg && (
            <p className={styles.formError} role="alert">
              {editErrorMsg}
            </p>
          )}
          <Select
            label={t('inquiries.source')}
            value={editForm.source}
            onChange={(e) => setEditField('source', e.target.value)}
            fullWidth
            options={SOURCES.map((s) => ({ value: s.value, label: t(s.labelKey) }))}
          />
          {/* Issue #63 — the card title (empty shows the client name).
              Issue #71 — it sits second, right after the source. */}
          <Input
            label={t('inquiries.cardTitle')}
            value={editForm.cardTitle}
            onChange={(e) => setEditField('cardTitle', e.target.value)}
            placeholder={t('inquiries.cardTitlePlaceholder')}
            helperText={t('inquiries.cardTitleHelper')}
            fullWidth
          />
          <Select
            label={t('inquiries.typeLabel')}
            value={editForm.type}
            onChange={(e) => setEditField('type', e.target.value as EditInquiryForm['type'])}
            placeholder={t('common.none')}
            fullWidth
            options={INQUIRY_TYPES.map((v) => ({ value: v, label: t(TYPE_LABEL_KEY[v]) }))}
          />
          <Input
            label={t('inquiries.channel')}
            value={editForm.channel}
            onChange={(e) => setEditField('channel', e.target.value)}
            placeholder={t('inquiries.channelPlaceholder')}
            fullWidth
            helperText={t('inquiries.channelHelper')}
          />
          <Input
            type="email"
            label={t('inquiries.clientEmail')}
            value={editForm.sourceEmail}
            onChange={(e) => {
              setEditField('sourceEmail', e.target.value);
              if (editEmailError) setEditEmailError(null);
            }}
            placeholder={t('inquiries.clientEmailPlaceholder')}
            fullWidth
            errorText={editEmailError}
          />
          <Input
            type="number"
            label={t('inquiries.fieldAmount')}
            value={editForm.estimatedAmount}
            onChange={(e) => {
              setEditField('estimatedAmount', e.target.value);
              if (editAmountError) setEditAmountError(null);
            }}
            placeholder={t('inquiries.amountPlaceholder')}
            inputMode="decimal"
            fullWidth
            errorText={editAmountError}
          />
          <ClientTypeahead
            clients={clientsQ.data ?? []}
            value={editForm.clientId}
            inn={editForm.inn}
            onInnChange={(v) => setEditField('inn', v)}
            onChange={(id) => setEditField('clientId', id)}
            isManufacturer={isManufacturer}
          />
          <TextArea
            label={t('inquiries.summary')}
            value={editForm.summary}
            onChange={(e) => setEditField('summary', e.target.value)}
            placeholder={t('inquiries.summaryPlaceholder')}
            rows={3}
            fullWidth
          />
          {isManufacturer && editShowsTkpIntegrators && (
            <IntegratorMultiSelect
              integrators={(integratorsQ.data ?? []).filter((i) => i.active)}
              value={editForm.tkpIntegratorIds}
              onChange={(ids) => setEditField('tkpIntegratorIds', ids)}
              label={t('inquiries.assignIntegrator')}
              helper={t('inquiries.assignHelper')}
            />
          )}
        </form>
      </Modal>

      {/* ── New Inquiry modal ──────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        title={t('inquiries.new')}
        description={t('inquiries.modalDesc')}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCreateOpen(false)}
              disabled={createMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="create-inquiry-form"
              loading={createMutation.isPending}
            >
              {t('common.create')}
            </Button>
          </>
        }
      >
        <form
          id="create-inquiry-form"
          onSubmit={submitCreate}
          onKeyDown={blockFormEnterSubmit}
          className={styles.form}
          noValidate
        >
          {createErrorMsg && (
            <p className={styles.formError} role="alert">
              {createErrorMsg}
            </p>
          )}
          <Select
            label={t('inquiries.source')}
            value={form.source}
            onChange={(e) => setField('source', e.target.value)}
            required
            fullWidth
            options={SOURCES.map((s) => ({ value: s.value, label: t(s.labelKey) }))}
          />
          {/* Issue #71 — the card title sits second, right after the source. */}
          <Input
            label={t('inquiries.cardTitle')}
            value={form.cardTitle}
            onChange={(e) => setField('cardTitle', e.target.value)}
            placeholder={t('inquiries.cardTitlePlaceholder')}
            helperText={t('inquiries.cardTitleHelper')}
            fullWidth
          />
          <Input
            label={t('inquiries.channel')}
            value={form.channel}
            onChange={(e) => setField('channel', e.target.value)}
            placeholder={t('inquiries.channelPlaceholder')}
            fullWidth
            helperText={t('inquiries.channelHelper')}
          />
          <Input
            type="email"
            label={t('inquiries.clientEmail')}
            value={form.sourceEmail}
            onChange={(e) => {
              setField('sourceEmail', e.target.value);
              if (emailError) setEmailError(null);
            }}
            placeholder={t('inquiries.clientEmailPlaceholder')}
            fullWidth
            errorText={emailError}
          />
          <Select
            label={t('inquiries.typeLabel')}
            value={form.type}
            onChange={(e) => setField('type', e.target.value as CreateInquiryForm['type'])}
            placeholder={t('common.none')}
            fullWidth
            options={INQUIRY_TYPES.map((v) => ({ value: v, label: t(TYPE_LABEL_KEY[v]) }))}
          />
          {/* Issue #58 — the client is reached by INN first: the operator
              types the INN here, we look the client up and (when there is no
              match) create a new one with the same INN. Issue #68 — the INN
              field is rendered by the client picker so its suggestions drop
              down under the INN itself. */}
          {/* TODO(#58, later): autofill the client name + email from
              checko.ru once the INN is entered — a separate stage. */}
          <ClientTypeahead
            clients={clientsQ.data ?? []}
            value={form.clientId}
            inn={form.inn}
            onInnChange={(v) => setField('inn', v)}
            onChange={(id) => setField('clientId', id)}
            isManufacturer={isManufacturer}
          />
          <TextArea
            label={t('inquiries.summary')}
            value={form.summary}
            onChange={(e) => setField('summary', e.target.value)}
            placeholder={t('inquiries.summaryPlaceholder')}
            rows={3}
            fullWidth
          />
          {isManufacturer && showsTkpIntegrators && (
            <IntegratorMultiSelect
              integrators={(integratorsQ.data ?? []).filter((i) => i.active)}
              value={form.tkpIntegratorIds}
              onChange={(ids) => setField('tkpIntegratorIds', ids)}
              label={t('inquiries.assignIntegrator')}
              helper={t('inquiries.assignHelper')}
            />
          )}
        </form>
      </Modal>
    </div>
  );
}

// ── List view ─────────────────────────────────────────────────────

function ListView({
  items,
  sort,
  onSort,
  onOpen,
}: {
  items: Inquiry[];
  sort: { key: 'client' | 'summary' | 'amount' | 'created' | 'age'; dir: 1 | -1 };
  onSort: (key: 'client' | 'summary' | 'amount' | 'created' | 'age') => void;
  onOpen: (inquiry: Inquiry) => void;
}) {
  const { t, locale } = useT();

  const header = (
    label: string,
    key?: typeof sort.key,
    numeric = false,
  ) => {
    if (!key) {
      return <th className={numeric ? styles.num : undefined}>{label}</th>;
    }
    const active = sort.key === key;
    const arrow = active ? (sort.dir === 1 ? '↑' : '↓') : '↕';
    const sortLabel = active
      ? sort.dir === 1
        ? t('inquiries.sortAsc')
        : t('inquiries.sortDesc')
      : t('inquiries.sortNone');
    return (
      <th
        className={`${styles.sortable} ${numeric ? styles.num : ''} ${active ? styles.activeSort : ''}`}
        aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
      >
        <button type="button" className={styles.sortBtn} onClick={() => onSort(key)} title={sortLabel}>
          {label}
          <span className={styles.arrow} aria-hidden="true">{arrow}</span>
        </button>
      </th>
    );
  };

  return (
    <div className={styles.tableWrap}>
      <div className={styles.tableCard}>
        <table className={styles.table} aria-label={t('inquiries.title')}>
          <thead>
            <tr>
              {header(t('inquiries.colClient'), 'client')}
              {header(t('inquiries.colSource'))}
              {header(t('inquiries.colType'))}
              {header(t('inquiries.colSummary'), 'summary')}
              {header(t('inquiries.colStatus'))}
              {header(t('inquiries.colIntegrator'))}
              {header(t('inquiries.colManager'))}
              {header(t('inquiries.colAmount'), 'amount', true)}
              {header(t('inquiries.colCreated'), 'created')}
              {header(t('inquiries.colAge'), 'age')}
            </tr>
          </thead>
          <tbody>
            {items.map((inq) => {
              const amount = visibleInquiryAmount(inq);
              const closed = inq.outcome !== 'NONE';
              const tone = ageTone(inq.createdAt);
              return (
                <tr
                  key={inq.id}
                  tabIndex={0}
                  onClick={() => onOpen(inq)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onOpen(inq);
                  }}
                >
                  <td>
                    <div className={styles.cellClient}>
                      {/* Issue #63 — the deal's own title (falls back to the
                          client name, shown as a link below). */}
                      {inq.cardTitle?.trim() && (
                        <span className={styles.cellCardTitle}>{inq.cardTitle.trim()}</span>
                      )}
                      <span className={styles.cellClientName}>
                        <EntityLink
                          kind="client"
                          id={inq.client?.id ?? inq.clientId}
                          name={inq.client?.companyName}
                          fallback={t('common.noClient')}
                        />
                      </span>
                      <span className={styles.cellRfid}>{inquiryRef(inq)}</span>
                    </div>
                  </td>
                  <td>
                    <SourceChip source={inq.source} />
                  </td>
                  <td>
                    <TypeChip type={inq.type} />
                  </td>
                  <td className={styles.cellSummary}>{inq.summary}</td>
                  <td>
                    <StatusPill
                      tone={
                        inq.outcome === 'WON'
                          ? 'outcome_won'
                          : inq.outcome === 'LOST'
                            ? 'outcome_lost'
                            : STAGE_PILL_TONE[inq.stage]
                      }
                    >
                      {inq.outcome === 'WON'
                        ? t('stage.won')
                        : inq.outcome === 'LOST'
                          ? t('stage.lost')
                          : t(STAGE_LABEL_KEY[inq.stage])}
                    </StatusPill>
                    {inq.paused && (
                      <Pause size={13} className={styles.pausedMark} aria-label={t('stage.paused')} />
                    )}
                  </td>
                  <td className={styles.cellIntegrator}>
                    <EntityLink
                      kind="integrator"
                      id={inq.assignedIntegrator?.id ?? inq.assignedIntegratorId}
                      name={inq.assignedIntegrator?.name}
                      fallback={t('common.notAssigned')}
                    />
                  </td>
                  <td className={styles.cellManager}>
                    {inq.assignedManager ? (
                      <span className={styles.managerName}>
                        <Avatar name={inq.assignedManager.name} size="sm" />
                        {inq.assignedManager.name}
                      </span>
                    ) : (
                      <span className={styles.managerUnassigned}>{t('common.notAssigned')}</span>
                    )}
                  </td>
                  <td className={`${styles.num} ${styles.cellSum}`}>
                    {amount !== null ? formatMoney(amount, locale) : '—'}
                  </td>
                  <td className={styles.cellDate}>{formatDate(inq.createdAt, locale)}</td>
                  <td className={styles.cellAge}>
                    {/* Issue #51 — day counter only from «ТКП»; early stages show progress dots. */}
                    {closed ? (
                      t('inquiries.closedLabel')
                    ) : stageShowsTimer(inq.stage) ? (
                      <span className={styles[`age_${tone}`] ?? ''}>{formatAge(inq.createdAt, locale)}</span>
                    ) : (
                      <StageProgress stage={inq.stage} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={styles.tableFoot}>
          <span>{t('inquiries.listFoot', { shown: items.length, total: items.length })}</span>
        </div>
      </div>
    </div>
  );
}

function SourceChip({ source }: { source: InquirySource }) {
  const { t } = useT();
  const map: Record<InquirySource, { cls: string; key: TranslationKey }> = {
    end_customer: { cls: styles.srcEnd, key: 'inquiries.sourceEndCustomer' },
    integrator: { cls: styles.srcInt, key: 'inquiries.sourceIntegrator' },
    partner_chkpz: { cls: styles.srcPart, key: 'inquiries.sourcePartnerChkpz' },
    email: { cls: styles.srcEmail, key: 'inquiries.sourceEmail' },
  };
  const { cls, key } = map[source] ?? { cls: '', key: 'inquiries.source' as TranslationKey };
  return <span className={`${styles.src} ${cls}`}>{t(key)}</span>;
}

function TypeChip({ type }: { type?: InquiryType | null }) {
  const { t } = useT();
  if (!type) return <span aria-hidden="true">—</span>;
  return (
    <span className={`${styles.src} ${styles.typeChip}`}>
      {t(TYPE_LABEL_KEY[type] ?? 'inquiryType.question')}
    </span>
  );
}

// ── Skeletons ─────────────────────────────────────────────────────

function BoardSkeleton() {
  const { t } = useT();
  return (
    <div className={styles.board}>
      {STAGE_ORDER.map((stage) => (
        <section key={stage} className={styles.skelCol} aria-label={t(STAGE_LABEL_KEY[stage])}>
          <div className={styles.skelColBody}>
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className={styles.skelCard}>
                <Skeleton width="60%" />
                <Skeleton width="100%" />
                <Skeleton width="40%" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className={styles.tableWrap}>
      <div className={styles.tableCard}>
        <div className={styles.skelRows}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.skelRow}>
              <Skeleton width="18%" />
              <Skeleton width="30%" />
              <Skeleton width="12%" />
              <Skeleton width="10%" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Integrator multi-select (TKP calculation, issue #59) ────────

/**
 * Checkbox multi-select over the active integrators. The chosen ids are stored
 * on the lead (`tkpIntegratorIds`); the outbound request to the integrators is
 * a later stage — here we only capture the selection.
 */
function IntegratorMultiSelect({
  integrators,
  value,
  onChange,
  label,
  helper,
}: {
  integrators: Integrator[];
  value: string[];
  onChange: (ids: string[]) => void;
  label: string;
  helper?: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const toggle = (id: string, on: boolean) =>
    onChange(on ? [...value, id] : value.filter((x) => x !== id));

  return (
    <div className={styles.multiSelect} ref={wrapRef}>
      <span className={styles.typeaheadLabel}>{label}</span>
      <button
        type="button"
        className={styles.multiSelectTrigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value.length ? undefined : styles.multiSelectPlaceholder}>
          {value.length
            ? t('inquiries.tkpIntegratorsChosen', { count: value.length })
            : t('inquiries.tkpIntegratorsPlaceholder')}
        </span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div className={styles.multiSelectDropdown} role="listbox" aria-multiselectable="true">
          {integrators.length === 0 ? (
            <div className={styles.typeaheadEmpty}>{t('inquiries.tkpIntegratorsEmpty')}</div>
          ) : (
            integrators.map((i) => (
              <div key={i.id} className={styles.multiSelectOption} role="option" aria-selected={value.includes(i.id)}>
                <Checkbox
                  label={i.name}
                  checked={value.includes(i.id)}
                  onChange={(e) => toggle(i.id, e.target.checked)}
                />
              </div>
            ))
          )}
        </div>
      )}
      {helper && <p className={styles.multiSelectHelper}>{helper}</p>}
    </div>
  );
}

// ── Client typeahead (create form) ──────────────────────────────

interface NewClientForm {
  companyName: string;
  inn: string;
  contactName: string;
  /** Issue #59 — optional contact position («Должность»). */
  positionNote: string;
  contactEmail: string;
  contactPhone: string;
}

function ClientTypeahead({
  clients,
  value,
  inn,
  onInnChange,
  onChange,
  isManufacturer,
  label,
  helper,
}: {
  clients: Client[];
  value: string;
  /** Issue #58 — INN typed in the base form (search / create by INN). */
  inn?: string;
  /**
   * Issue #68 — when given, the INN field is rendered by this component (so its
   * suggestions can drop down under the INN input); omitted by the plain
   * client picker that has no INN field.
   */
  onInnChange?: (v: string) => void;
  onChange: (id: string) => void;
  isManufacturer: boolean;
  label?: string;
  helper?: string;
}) {
  const { t } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();

  const selected = value ? clients.find((c) => c.id === value) ?? null : null;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Issue #62 — the INN suggestions are dismissible (outside click / Escape)
  // like any dropdown; they re-arm as soon as the INN changes again.
  const [dismissedInn, setDismissedInn] = useState('');

  // Issue #68 — the INN field drives the lookup only while the search box is
  // empty; a digits-only INN is matched against the INN alone (`innOnly`), so
  // a phone carrying the same digits never shows up as a suggestion.
  const innValue = (inn ?? '').trim();
  const innDriven = !query.trim() && innValue.length > 0;
  const innOnly = innDriven && /^\d+$/.test(innValue);

  // Issue #58 — a saved client is matched by name OR by INN. While the search
  // box is empty the INN typed in the base form drives the lookup, so entering
  // an INN both finds an existing client and pre-fills the “create new” panel.
  const term = query.trim() || innValue;
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(term), 300);
    return () => window.clearTimeout(id);
  }, [term]);

  const searchQ = useQuery<Client[]>({
    queryKey: ['clients', 'search', debounced, innOnly],
    queryFn: () =>
      api.get<Client[]>('/clients', {
        query: innOnly ? { search: debounced, innOnly: true } : { search: debounced },
      }),
    enabled: debounced.length > 0,
  });
  const results = searchQ.data ?? [];

  // Issue #62 — the INN drives *suggestions*, not the link. Typing an INN in
  // the base form filters the saved clients (the search matches on the INN too)
  // and the operator picks one explicitly; two clients with look-alike INNs are
  // therefore both offered instead of one arbitrary match being linked quietly.
  // Remembers the INN we last reacted to, so a re-render (or an explicit pick)
  // does not re-run the unlink below.
  const linkedInnRef = useRef<string>(innValue);

  // Any real change to the INN drops whatever client was linked — whether it
  // came from a previous INN pick or a manual pick — so the chip never goes
  // stale. A pick made without touching the INN is untouched: this only fires
  // when the value actually changes.
  useEffect(() => {
    if (linkedInnRef.current === innValue) return;
    linkedInnRef.current = innValue;
    if (value) onChange('');
  }, [innValue, value, onChange]);

  // INN-driven suggestions: while the search box is untouched, a typed INN
  // offers the matching clients in the dropdown and the operator clicks one.
  // No match → no suggestions and nothing linked (never a silent auto-link).
  useEffect(() => {
    setDismissedInn('');
  }, [innValue]);
  const innSuggestions =
    !selected &&
    !query.trim() &&
    innValue.length > 0 &&
    innValue !== dismissedInn &&
    debounced === innValue &&
    results.length > 0;
  // Issue #68 — the two dropdowns are anchored to different fields: the name
  // search drops down under the client input, the INN suggestions under the
  // INN input (they never show at the same time).
  const showSearchDropdown = open && query.trim().length > 0;
  const showDropdown = showSearchDropdown || innSuggestions;

  // ── Inline create ─────────────────────────────────────────────
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState<NewClientForm>({
    companyName: '',
    inn: '',
    contactName: '',
    positionNote: '',
    contactEmail: '',
    contactPhone: '',
  });
  const setNewField = <K extends keyof NewClientForm>(k: K, v: NewClientForm[K]) =>
    setNewClient((prev) => ({ ...prev, [k]: v }));

  const createMutation = useMutation({
    mutationFn: (payload: NewClientForm) => {
      const body: Record<string, unknown> = {
        companyName: payload.companyName.trim(),
      };
      if (payload.inn.trim()) body.inn = payload.inn.trim();
      // Issue #59 — the contact fields are optional: send only what is filled
      // (the API keeps them empty when omitted).
      if (payload.contactName.trim()) body.contactName = payload.contactName.trim();
      if (payload.positionNote.trim()) body.positionNote = payload.positionNote.trim();
      if (payload.contactEmail.trim()) body.contactEmail = payload.contactEmail.trim();
      if (payload.contactPhone.trim()) body.contactPhone = payload.contactPhone.trim();
      return api.post<Client>('/clients', body);
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(t('clients.toastCreated'), created.companyName);
      onChange(created.id);
      setCreating(false);
      setOpen(false);
      setQuery('');
    },
    onError: (err) => {
      // Issue #58 — a saved client with this INN already exists: link it
      // instead of dropping the operator into an error and making them
      // re-create a client that is already in the CRM.
      if (err instanceof ApiError && err.status === 409) {
        const existingId = (err.data as { clientId?: string } | null)?.clientId;
        if (existingId) {
          void queryClient.invalidateQueries({ queryKey: ['clients'] });
          onChange(existingId);
          setCreating(false);
          setOpen(false);
          setQuery('');
          return;
        }
      }
      const msg = err instanceof ApiError ? err.message : t('clients.errorCreate');
      toast.error(t('clients.toastCreateFail'), msg);
    },
  });

  const startCreate = () => {
    setNewClient({
      companyName: query.trim(),
      // Seed the new client with the INN already typed in the base form (#58):
      // “entered an INN → no client in the CRM → create one with that INN”.
      inn: (inn ?? '').trim(),
      contactName: '',
      positionNote: '',
      contactEmail: '',
      contactPhone: '',
    });
    setCreating(true);
    setOpen(false);
  };

  // Issue #59 — the contact fields are optional; only the company name is
  // needed to create the client.
  const newRequiredOk = newClient.companyName.trim();

  // Issue #68 — the wrapper spans the INN field and the client picker, so a
  // click in either one counts as “inside” and does not dismiss the dropdown;
  // an outside click or Escape closes it (the INN re-arms on the next change).
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showDropdown) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setDismissedInn(innValue);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setDismissedInn(innValue);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showDropdown, innValue]);

  // The suggestion rows are shared by the name search and the INN lookup — both
  // dropdowns list the same `results`, only their anchor field differs.
  const renderOptions = () =>
    searchQ.isLoading ? (
      <div className={styles.typeaheadEmpty}>…</div>
    ) : results.length === 0 ? (
      <div className={styles.typeaheadEmpty}>{t('inquiries.clientNoResults')}</div>
    ) : (
      results.map((c) => (
        <button
          key={c.id}
          type="button"
          role="option"
          aria-selected="false"
          className={styles.typeaheadOption}
          onClick={() => {
            onChange(c.id);
            setOpen(false);
            setQuery('');
          }}
        >
          <span>{c.companyName}</span>
          <span className={styles.typeaheadOptionMeta}>
            {[c.inn, c.contactPhone].filter(Boolean).join(' · ')}
          </span>
        </button>
      ))
    );

  return (
    <div className={styles.clientPicker} ref={wrapRef}>
      {/* Issue #68 — the INN suggestions live under the INN input itself. */}
      {onInnChange && (
        <div className={styles.innField}>
          <Input
            label={t('clients.fieldInn')}
            value={inn ?? ''}
            onChange={(e) => onInnChange(e.target.value)}
            placeholder={t('inquiries.innPlaceholder')}
            helperText={t('inquiries.innHelper')}
            inputMode="numeric"
            fullWidth
            role="combobox"
            aria-expanded={innSuggestions}
          />
          {innSuggestions && (
            <div className={styles.typeaheadDropdown} role="listbox">
              {renderOptions()}
            </div>
          )}
        </div>
      )}

      <div className={styles.typeahead}>
        <span className={styles.typeaheadLabel}>{label ?? t('inquiries.client')}</span>

        {selected ? (
          <div className={styles.clientChip}>
            <div>
              <div className={styles.clientChipName}>{selected.companyName}</div>
              <div className={styles.clientChipMeta}>
                {[selected.inn, selected.contactPhone, selected.contactEmail]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <button
              type="button"
              className={styles.clientChipClear}
              onClick={() => onChange('')}
              aria-label={t('inquiries.clientClear')}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <>
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={t('inquiries.clientSearchPlaceholder')}
              helperText={helper ?? t('inquiries.clientHelper')}
              fullWidth
              role="combobox"
              aria-expanded={showSearchDropdown}
            />
            {showSearchDropdown && (
              <div className={styles.typeaheadDropdown} role="listbox">
                {renderOptions()}
                {isManufacturer && query.trim().length > 0 && !searchQ.isLoading && (
                  <button type="button" className={styles.typeaheadCreate} onClick={startCreate}>
                    {t('inquiries.clientCreateNew', { name: query.trim() })}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {creating && !selected && (
          <div className={styles.inlineCreate}>
            <p className={styles.inlineCreateDesc}>{t('inquiries.clientNewDesc')}</p>
            <Input
              label={t('clients.fieldCompanyName')}
              value={newClient.companyName}
              onChange={(e) => setNewField('companyName', e.target.value)}
              required
              fullWidth
            />
            <Input
              label={t('clients.fieldInn')}
              value={newClient.inn}
              onChange={(e) => setNewField('inn', e.target.value)}
              fullWidth
            />
            <Input
              label={t('clients.fieldContactName')}
              value={newClient.contactName}
              onChange={(e) => setNewField('contactName', e.target.value)}
              fullWidth
            />
            <Input
              label={t('clients.fieldPosition')}
              value={newClient.positionNote}
              onChange={(e) => setNewField('positionNote', e.target.value)}
              fullWidth
            />
            <Input
              label={t('clients.fieldContactEmail')}
              type="email"
              value={newClient.contactEmail}
              onChange={(e) => setNewField('contactEmail', e.target.value)}
              fullWidth
            />
            <Input
              label={t('clients.fieldContactPhone')}
              value={newClient.contactPhone}
              onChange={(e) => setNewField('contactPhone', e.target.value)}
              fullWidth
            />
            <div className={styles.inlineCreateActions}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCreating(false)}
                disabled={createMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                loading={createMutation.isPending}
                disabled={!newRequiredOk}
                onClick={() => createMutation.mutate(newClient)}
              >
                {t('common.create')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
